import React, {useMemo} from 'react'
import {useIntl} from 'react-intl'

interface Video {
  title: string
  url: string
  duration: string
}

interface DemoPanelProps {
  // Common props
  totalDuration: string
  videosCount: number

  // Player page specific props
  videos?: Video[]
  currentVideoIndex?: number
  isPlaying?: boolean
  isPanelMinimized?: boolean
  onVideoSelect?: (index: number) => void
  onPlayFirstVideo?: () => void
  onCollapsePanel?: () => void

  // Topics page specific props
  selectedSubtopics?: string[]
  mainTopic?: string
  videoDurations?: {[key: string]: string}

  // Mode
  mode: 'player' | 'topics'
}

// Move titleKeyMap outside component to avoid dependency issues
const titleKeyMap: {[key: string]: string} = {
  'Create A New Batch/Add To Existing Batch': 'DEMO_TOPICS_SUBTOPICS_CREATE_BATCH',
  'Update Batch Information': 'DEMO_TOPICS_SUBTOPICS_UPDATE_BATCH',
  'Create Shipping Labels': 'DEMO_TOPICS_SUBTOPICS_CREATE_LABELS',
  'Notify Origin Partner & Schedule Pick-Up': 'DEMO_TOPICS_SUBTOPICS_NOTIFY_PARTNER',
}

const DemoPanel: React.FC<DemoPanelProps> = ({
  totalDuration,
  videosCount,
  videos = [],
  currentVideoIndex = -1,
  isPlaying = false,
  isPanelMinimized = false,
  onVideoSelect,
  onPlayFirstVideo,
  onCollapsePanel,
  selectedSubtopics = [],
  mainTopic = '',
  videoDurations = {},
  mode,
}) => {
  const intl = useIntl()

  // Get translated video titles for current language
  const translatedVideos = useMemo(() => {
    return videos.map((video) => ({
      ...video,
      title: titleKeyMap[video.title]
        ? intl.formatMessage({id: titleKeyMap[video.title]})
        : video.title,
    }))
  }, [videos, intl])

  // Collapsed state for player mode
  if (mode === 'player' && isPanelMinimized) {
    return (
      <button onClick={onCollapsePanel} className='demo-collapsed-panel'>
        <img
          src='/media/gori/demo/collapse.svg'
          alt='Expand'
          width='16'
          height='16'
          className='demo-collapsed-panel-icon'
        />
      </button>
    )
  }

  return (
    <div
      className={
        mode === 'player'
          ? 'demo-panel-player d-flex flex-column'
          : 'd-flex flex-column flex-grow-1'
      }
    >
      {/* Panel Header */}
      <div className='demo-panel-header d-flex align-items-center'>
        {/* Collapse Button - Player mode only */}
        {mode === 'player' && (
          <button onClick={onCollapsePanel} className='btn p-0 demo-collapse-btn'>
            <img src='/media/gori/demo/collapse.svg' alt='Collapse' width='16' height='16' />
          </button>
        )}

        {/* Centered Title and Summary */}
        <div className='flex-grow-1 text-center'>
          <h3 className='demo-panel-title'>{intl.formatMessage({id: 'DEMO_PLAYER_MY_DEMO'})}</h3>
          <div className='demo-panel-summary'>
            {videosCount} {mode === 'player' ? 'topics' : 'subtopics'} | {totalDuration}
          </div>
        </div>
      </div>

      <div className='flex-grow-1'>
        {/* Player Mode Content */}
        {mode === 'player' && (
          <>
            {/* Main Topic */}
            <div
              className={`demo-main-topic d-flex align-items-center justify-content-between ${
                currentVideoIndex >= 0 ? 'active' : ''
              }`}
              onClick={onPlayFirstVideo}
            >
              <div className='demo-main-topic-content'>
                <div className='demo-main-topic-title'>
                  {intl.formatMessage({id: 'DEMO_PLAYER_MAIN_TOPIC'})}
                </div>
                <div className='demo-main-topic-duration'>{totalDuration}</div>
              </div>
              <div className='demo-play-button'>
                <img
                  src={
                    currentVideoIndex >= 0
                      ? '/media/gori/demo/play_active.svg'
                      : '/media/gori/demo/play_inactive.svg'
                  }
                  alt='Play'
                  width='32'
                  height='32'
                />
              </div>
            </div>

            {/* Single Subtopic Arrow */}
            <div className='demo-subtopic-arrow-container'>
              <img
                src='/media/gori/demo/subtopic-arrow.svg'
                alt='subtopic arrow'
                className='demo-subtopic-arrow'
              />
            </div>

            {/* Subtopics */}
            <div className='demo-panel-subtopics'>
              {translatedVideos.map((video: Video, index: number) => (
                <div
                  key={index}
                  className={`demo-subtopic d-flex align-items-start demo-subtopic-item ${
                    index === currentVideoIndex ? 'active' : ''
                  }`}
                  onClick={() => onVideoSelect?.(index)}
                >
                  <div className='flex-grow-1 demo-subtopic-content'>
                    <div
                      className={`demo-subtopic-title ${
                        index === currentVideoIndex ? 'active' : ''
                      }`}
                    >
                      {video.title}
                    </div>
                    <div className='demo-subtopic-duration'>{video.duration}</div>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Topics Mode Content */}
        {mode === 'topics' && (
          <>
            {selectedSubtopics.length > 0 ? (
              <>
                {/* Main Topic */}
                <div className='demo-main-topic d-flex align-items-center justify-content-between'>
                  <div className='demo-main-topic-content'>
                    <div className='demo-main-topic-title'>{mainTopic}</div>
                    <div className='demo-main-topic-duration'>{totalDuration}</div>
                  </div>
                  <div className='demo-play-button'>
                    <img
                      src='/media/gori/demo/play_inactive.svg'
                      alt='Play'
                      width='32'
                      height='32'
                    />
                  </div>
                </div>

                {/* Single Subtopic Arrow */}
                <div className='demo-subtopic-arrow-container'>
                  <img
                    src='/media/gori/demo/subtopic-arrow.svg'
                    alt='subtopic arrow'
                    className='demo-subtopic-arrow'
                  />
                </div>

                {/* Subtopics */}
                <div className='demo-panel-subtopics'>
                  {selectedSubtopics.map((subtopic, index) => {
                    // Translate subtopic name using titleKeyMap
                    const translatedSubtopic = titleKeyMap[subtopic]
                      ? intl.formatMessage({id: titleKeyMap[subtopic]})
                      : subtopic

                    return (
                      <div
                        key={index}
                        className='demo-subtopic d-flex align-items-start demo-subtopic-item'
                      >
                        <div className='flex-grow-1 demo-subtopic-content'>
                          <div className='demo-subtopic-title'>{translatedSubtopic}</div>
                          <div className='demo-subtopic-duration'>
                            {videoDurations[subtopic] || 'Loading...'}
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
              </>
            ) : (
              /* Placeholder when no selections */
              <div className='demo-placeholder'>
                <img
                  src='/media/gori/demo/demo_no_topics_selected_placeholder.webp'
                  alt='No topics selected'
                  className='demo-placeholder-image'
                />
                <div className='demo-placeholder-text'>
                  {intl.formatMessage({id: 'DEMO_TOPICS_PLACEHOLDER_TEXT'})}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}

export default DemoPanel
